import { Injectable } from '@angular/core';
import {CrudService} from './services';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {GpsLoadResponseFileImportResult, PartnerWalletLoadImport} from './dto/gps-load-response-file-import-result';
import {PartnerWallet} from './dto/partnerWallet';
import {DataListModel} from './models/data-list-model';
import {Invoice} from './dto/invoice';
import {PartnerWalletTransaction} from './dto/partnerWalletTransaction';
import {PartnerWalletLoadModel} from './models/partner-wallet-load-model';
import {DatePipe} from '@angular/common';
import {Upload} from './dto/upload';
import {OrderImport} from './dto/order-import';
import {TrustPayTransaction} from './dto/trustPayTransaction';
import {toISODateString} from './providers/app-date-adapter';
import {AppUserPartnerWalletAccess, AppUserPartnerWalletAccessSlim} from './dto/app-user-partner-wallet-access-slim';
import {LoadInfo} from "./dto/loadInfo";

@Injectable({
  providedIn: 'root'
})
export class PartnerWalletService implements CrudService<PartnerWallet> {

  readonly apiPath = 'partner-wallet/';

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
  ) { }

  getPendingInvitesCount(): Observable<number> {
    return this.http.get( this.apiPath + 'pending-invites-count', {headers: {'skip-error-snack': 'true'} } ) as Observable<number>;
  }

  getPendingInvites(): Observable<AppUserPartnerWalletAccessSlim[]> {
    return this.http.get( this.apiPath + 'pending-invites', {headers: {'skip-error-snack': 'true'} } ) as Observable<AppUserPartnerWalletAccessSlim[]>;
  }

  getIncompleteInvites(): Observable<AppUserPartnerWalletAccessSlim[]> {
    return this.http.get( this.apiPath + 'incomplete-invites', {headers: {'skip-error-snack': 'true'} } ) as Observable<AppUserPartnerWalletAccessSlim[]>;
  }

  getFinishedInvites(): Observable<AppUserPartnerWalletAccessSlim[]> {
    return this.http.get( this.apiPath + 'finished-invites', {headers: {'skip-error-snack': 'true'} } ) as Observable<AppUserPartnerWalletAccessSlim[]>;
  }

  uploadPartnerLoadFile(file: File, partnerWalletId: number): Observable<PartnerWalletLoadImport> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.apiPath + 'upload-partner-load-file/' + partnerWalletId, file) as Observable<PartnerWalletLoadImport>;
  }
  get(id: any) {
    return this.http.get(this.apiPath + id) as Observable<PartnerWallet>;
  }

  getLoadInfo(id: any) {
    return this.http.get(this.apiPath + id + "/load-info") as Observable<LoadInfo>;
  }

  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string) {
    const params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set( 'sortDirection', sortDirection )
      .set( 'someAdditionalData', someAdditionalData );
    return this.http.get(this.apiPath, { params }) as Observable<DataListModel<PartnerWallet>>;
  }

  uploadGpsResponseImportCsv(file: File): Observable<GpsLoadResponseFileImportResult[]> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.apiPath + 'gps-response-upload', file) as Observable<GpsLoadResponseFileImportResult[]>;
  }


  add(item: PartnerWallet): Observable<Object> {
    throw new Error('Method not implemented.');
  }
  update(item: PartnerWallet) {
    return this.http.patch(this.apiPath, item) as Observable<PartnerWallet>;
  }

  delete(id: any) {
    return this.deleteMany([id]);
  }
  restore(id: any) {
    return this.restoreMany([id]);
  }

  deleteMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }

  restoreMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }


  loadWalletByAdmin(partnerWalletId: number, amount: number, description: string): Observable<string> {

    return this.http.post(this.apiPath + 'load-by-admin', {
      amount,
      description,
      partnerWalletId
    }) as Observable<string>;
  }

  getTransactions(partnerWalletId: number, pageIndex: number,
                  pageSize: number,
                  sortField: string,
                  sortDirection: string,
                  filterString: string,
                  dateFilterCols: string[],
                  startFilterDate: Date,
                  endFilterDate: Date,
                  someAdditionalData: string): Observable<DataListModel<PartnerWalletTransaction>> {
      const params = new HttpParams()
        .set('page', pageIndex.toString())
        .set('pageSize', pageSize.toString())
        .set('filterString', filterString)
        .set('sortField', sortField)
        .set('startFilterDate', toISODateString(startFilterDate))
        .set('endFilterDate', toISODateString(endFilterDate))
        .set( 'sortDirection', sortDirection )
        .set( 'someAdditionalData', someAdditionalData );

    return this.http.get(this.apiPath + partnerWalletId + '/transactions-list', { params }) as Observable<DataListModel<PartnerWalletTransaction>>;
  }

  createWallet(partnerWallet: PartnerWallet): Observable<PartnerWallet> {
    return this.http.put(this.apiPath, partnerWallet) as Observable<PartnerWallet>;
  }

  getPendingCardLoads(partnerWalletId: number): Observable<PartnerWalletLoadModel[]> {
    return this.http.get(this.apiPath + partnerWalletId + '/pending-card-loads') as Observable<PartnerWalletLoadModel[]>;
  }

  balanceAdjustWalletByAdmin(partnerWalletId: number, amount: number, description: string) {
    return this.http.post(this.apiPath + 'balance-adjust-by-admin', {
      amount,
      description,
      partnerWalletId
    }) as Observable<string>;
  }

  transferFunds(amount: number, sourceId: number, destinationId: number, description: string) {
    return this.http.post(this.apiPath + 'transfer-funds', {
      amount,
      description,
      sourceWalletId: sourceId,
      destinationWalletId: destinationId
    }) as Observable<string>;
  }

  downloadStatement( partnerWalletId: number, from: Date, to: Date, format: string ): Observable<HttpResponse<Blob>> {
    return this.http.get( this.apiPath + 'statement/' +
      partnerWalletId + '/' +
      this.datePipe.transform(from, 'yyyyMMdd') + '/' +
      this.datePipe.transform(to, 'yyyyMMdd') + '/' +
      format
      , { params: null, responseType: 'blob', observe: 'response' } );
  }

  uploadLoadFileDocuments(files: File[], loadId: number): Observable<string> {
    const data = new FormData();
    let i = 0;
    files.forEach((f) => {
      data.append('file_' + i, f, f.name);
      i++;
    });
    return this.http.post(this.apiPath + 'partner-load-document/' + loadId, data) as Observable<string>;
  }

  downloadPartnerLoadDocument( loadDocId: number ): Observable<HttpResponse<Blob>> {
    return this.http.get( this.apiPath + 'partner-load-document/' + loadDocId + '/download', { params: null, responseType: 'blob', observe: 'response' } );
  }


  deletePartnerLoadDocument(documentId: number): Observable<string> {
    return this.http.delete(this.apiPath + 'partner-load-document/' + documentId) as Observable<string>;
  }

  getTransaction(walletId: number, txnId: number) {
    return this.http.get(this.apiPath + walletId + '/txn/' + txnId) as Observable<PartnerWalletTransaction>;
  }

  inviteUser(partnerWalletId: number, email: string): Observable<string> {
    const data = {email };
    return this.http.post(this.apiPath + 'invite-user/' + partnerWalletId, data) as Observable<string>;
  }

  getUsers(partnerWalletId: number): Observable<DataListModel<AppUserPartnerWalletAccessSlim>> {
    return this.http.get(this.apiPath + partnerWalletId + '/users') as Observable<DataListModel<AppUserPartnerWalletAccessSlim>>;
  }

  getInvite(inviteId: number): Observable<AppUserPartnerWalletAccess> {
    return this.http.get(this.apiPath + 'invite/' + inviteId) as Observable<AppUserPartnerWalletAccess>;
  }

  approveInvite(inviteId: number): Observable<AppUserPartnerWalletAccessSlim> {
    return this.http.post(this.apiPath + 'approve-invite/' + inviteId, {oh: 1}) as Observable<AppUserPartnerWalletAccessSlim>;
  }

  declineInviteWithReason(inviteId: number, reason: string): Observable<AppUserPartnerWalletAccessSlim> {
    return this.http.post(this.apiPath + 'decline-invite/' + inviteId, {reason}) as Observable<AppUserPartnerWalletAccessSlim>;
  }
}
